import React from "react";
import {observer} from "mobx-react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import {
    PlanExtensionSimulateHireEntity
} from "@dropDesk/domain/entities/plan_extensions/plan_extension_simulate_hire.entity";
import {
    BillingCard,
    Divider,
    ExplanatoryText,
    MainValue,
    MainValueRow,
    MainValueSuffix,
    NextBillingCard,
    NextBillingDate,
    NextBillingLabel,
    NextBillingNote,
    SummaryLabel,
    SummaryList,
    SummaryRow,
    SummaryValue,
    SummaryLabelGroup,
    NextBillingNoteStrong,
    BillingCardSpacer,
    BillingNote,
} from "./styled";
import ButtonMain from "@dropDesk/presentation/components/buttons/button_main";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {formatCurrency} from "@dropDesk/utils/helpers/common";
import {formatDateToDDMMYYYY} from "@dropDesk/utils/helpers/date_helper";

type Props = {
    extension: PlanExtensionCatalogEntity;
    simulate: PlanExtensionSimulateHireEntity;
    loading: boolean;
    onConfirm?: () => void;
    style?: React.CSSProperties;
}

const ExtensionBillingSummary = observer(({extension, simulate, loading, onConfirm, style}: Props) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const hasTrial = simulate.trialDaysApplied > 0;

    return (
        <BillingCard background={'transparent'} border={colors.border} style={style}>
            <MainValueRow>
                <MainValue color={colors.text}>
                    {formatCurrency(simulate.firstCycleValue)}
                </MainValue>
                <MainValueSuffix color={colors.text}>
                    {hasTrial ? 'no vencimento' : 'hoje'}
                </MainValueSuffix>
            </MainValueRow>

            {hasTrial ? (
                <NextBillingCard
                    background={`${colors.success}10`}
                    border={`${colors.success}30`}
                    style={{marginBottom: 0, marginTop: 8}}
                >
                    <NextBillingLabel color={colors.success}>
                        <DropDeskIcon icon={ICONS_DROPDESK.check} size={13} color={colors.success}/>
                        {' '}Período gratuito incluído
                    </NextBillingLabel>
                    <NextBillingNote color={colors.text} style={{marginTop: 4}}>
                        Uma cobrança de <NextBillingNoteStrong color={colors.text}>{formatCurrency(simulate.firstCycleValue)}</NextBillingNoteStrong> será gerada agora com vencimento em <NextBillingNoteStrong color={colors.text}>{formatDateToDDMMYYYY(new Date(simulate.paymentDueDate))}</NextBillingNoteStrong>, após seus <NextBillingNoteStrong color={colors.text}>{simulate.trialDaysApplied} dias gratuitos</NextBillingNoteStrong>. Se cancelar antes disso, não será cobrado nada.
                    </NextBillingNote>
                </NextBillingCard>
            ) : (
                <ExplanatoryText color={colors.text}>
                    Valor proporcional referente aos {simulate.chargedDays} dias restantes.
                </ExplanatoryText>
            )}

            <Divider color={colors.border}/>

            <SummaryList>
                <SummaryRow>
                    <SummaryLabel color={colors.text}>Extensão:</SummaryLabel>
                    <SummaryValue color={colors.text}>{extension.name}</SummaryValue>
                </SummaryRow>

                <SummaryRow>
                    <SummaryLabelGroup>
                        <SummaryLabel color={colors.text}>Valor mensal da extensão</SummaryLabel>
                    </SummaryLabelGroup>
                    <SummaryValue color={colors.text}>
                        {formatCurrency(extension.defaultPrice)}
                    </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                    <SummaryLabel color={colors.text}>Recorrência</SummaryLabel>
                    <SummaryValue color={colors.text}>Mensal</SummaryValue>
                </SummaryRow>

                <SummaryRow>
                    <SummaryLabelGroup style={{flexDirection: 'column', alignItems: 'flex-start', gap: 2}}>
                        <SummaryLabel color={colors.text}>Melhor dia de pagamento</SummaryLabel>
                        <BillingNote color={colors.hint}>(vencimento da sua assinatura)</BillingNote>
                    </SummaryLabelGroup>
                    <SummaryValue color={colors.text}>
                        Dia {simulate.extension.dayDue}
                    </SummaryValue>
                </SummaryRow>

                <Divider color={colors.border}/>

                <SummaryRow>
                    <SummaryLabel color={colors.text}>Dias faturados</SummaryLabel>
                    <SummaryValue color={colors.text}>{simulate.chargedDays} dias</SummaryValue>
                </SummaryRow>

                <SummaryRow>
                    <SummaryLabel color={colors.text}>Período</SummaryLabel>
                    <SummaryValue color={colors.text}>{simulate.period}</SummaryValue>
                </SummaryRow>

                <SummaryRow>
                    <SummaryLabel color={colors.text}>Início da cobrança</SummaryLabel>
                    <SummaryValue color={colors.text}>{formatDateToDDMMYYYY(new Date(simulate.instantDate))}</SummaryValue>
                </SummaryRow>

                <SummaryRow>
                    <SummaryLabel color={colors.text}>Valor do ajuste proporcional</SummaryLabel>
                    <SummaryValue color={colors.text}>
                        {formatCurrency(simulate.firstCycleValue)}
                    </SummaryValue>
                </SummaryRow>
            </SummaryList>

            <BillingCardSpacer/>

            {!hasTrial && <NextBillingCard background={`${colors.success}10`} border={`${colors.success}30`}>
                <NextBillingLabel color={colors.success}>
                    <DropDeskIcon icon={ICONS_DROPDESK.calendar} size={14} color={colors.success}/>
                    {' '}Próxima cobrança
                </NextBillingLabel>
                <NextBillingDate color={colors.text}>{formatDateToDDMMYYYY(new Date(simulate.nextDueDate))}</NextBillingDate>
                <NextBillingNote color={colors.hint}>
                    Após essa data será cobrado o valor integral mensal de <NextBillingNoteStrong
                    color={colors.text}>{formatCurrency(extension.defaultPrice)}</NextBillingNoteStrong>.
                </NextBillingNote>
            </NextBillingCard>}

            {onConfirm && <ButtonMain
                widthPercentage={100}
                letterFontSize={14}
                height={46}
                loading={loading}
                disabled={loading}
                color={colors.accent}
                type="button"
                letter="Confirmar ativação da extensão"
                onClick={onConfirm}
            />}
        </BillingCard>
    );
});

export default ExtensionBillingSummary;
